import { useMutation, UseMutationOptions } from '@tanstack/react-query';

import { useMutationHTTPRequest } from 'shared_DEPRECATED/hooks';

import {
  ChallengesPostType,
  CreateCustomChallengesPostType,
} from 'features/challenge/config/types';
import { useUserId } from 'features/user/hooks/useUserId';

type TUseCreateCustomChallengesMutation<TChallengeType> = {
  options?: UseMutationOptions<
    { challenges: TChallengeType[] },
    Error,
    ChallengesPostType<TChallengeType>,
    { challenges: TChallengeType[] }
  >;
};

export type ChallengeWithId = {
  challengeId?: string;
  [key: string]: any;
};

export const useCreateCustomChallengesMutation = <
  TChallengeType extends ChallengeWithId
>({
  options,
}: TUseCreateCustomChallengesMutation<TChallengeType>) => {
  const { request } = useMutationHTTPRequest();
  const userId = useUserId();

  return useMutation(
    async ({
      challenges,
      parameters,
    }: CreateCustomChallengesPostType<TChallengeType>) => {
      const challengeArray = Array.isArray(challenges)
        ? challenges
        : [challenges];

      // Check if all challenges have challenge_id
      const challengesWithId = challengeArray.filter(
        (challenge) => challenge.challengeId
      );
      const challengesWithoutId = challengeArray.filter(
        (challenge) => !challenge.challengeId
      );

      // Validate that all challenges are either with or without challenge_id
      if (challengesWithId.length > 0 && challengesWithoutId.length > 0) {
        throw new Error(
          'All challenges must either have challenge_id (for editing) or no challenge_id (for creation). Mixed states are not allowed.'
        );
      }

      // Determine the endpoint based on whether challenges have IDs
      const isEdit = challengesWithId.length === challengeArray.length;
      const endpoint = isEdit
        ? '/api/challenges/edit'
        : '/api/challenges/create';

      return await request({
        url: endpoint,
        body: {
          targetUserId: userId,
          challenges: challengeArray,
          parameters,
        },
      });
    },
    {
      ...options,
    }
  );
};
